import { Component, HostListener, inject, OnInit } from '@angular/core';
import { HelperTextService } from '../../../../core/services/helper-text.service';
import { HelpTextModel } from 'src/hub_schema/hubTypes';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs';
import { ErrorService } from '../../../../core/services/error.service';
import { AddHelperTextDialogComponent } from './add-helper-text-dialog/add-helper-text-dialog.component';
import { urlPattern } from '../../../../shared/helpers/urlPattern';
import { DeleteConfirmationDialogComponent } from '../../../../shared/components/delete-confirmation-dialog/delete-confirmation-dialog.component';

@Component({
  selector: 'app-helper-text-tab',
  templateUrl: './helper-text-tab.component.html',
  styleUrl: './helper-text-tab.component.scss',
  standalone: false,
})
export class HelperTextTabComponent implements OnInit {
    private helperTextService: HelperTextService = inject(HelperTextService);
    public errorService: ErrorService = inject(ErrorService);
    private dialogService: MatDialog = inject(MatDialog);

    public helperTexts: HelpTextModel[] | undefined;
    public helperTextForms: FormArray | undefined;

    public isSaving: boolean = false;

    public get isSaveButtonDisabled(): boolean {
        if (!this.helperTextForms) {
            return true;
        }
        return this.helperTextForms.invalid || this.helperTextForms.pristine;
    }

    private populateHelperTexts(): void {
        this.helperTextService.getAllHelperTexts().pipe(take(1))
            .subscribe((helperTexts: HelpTextModel[]) => {
                this.helperTexts = helperTexts;
                this.helperTextForms = new FormArray<any>([]);

                for (const text of this.helperTexts) {
                    const formGroup = new FormGroup({
                        helpTextId: new FormControl({value: text.helpTextId, disabled: true}),
                        context: new FormControl(text.context),
                        label: new FormControl(text.label, Validators.required),
                        contentHtmlShort: new FormControl(text.contentHtmlShort, Validators.required),
                        link: new FormControl(text.link, Validators.pattern(urlPattern)),
                    });

                    this.helperTextForms.push(formGroup);
                }
            });
    }

    public ngOnInit(): void {
        this.populateHelperTexts();
    }

    public resetRequested(): void {
        const dialogRef: MatDialogRef<DeleteConfirmationDialogComponent> = this.dialogService.open(DeleteConfirmationDialogComponent, {
            maxWidth: 'none',
            data: {
                confirmButtonText: "OK",
                warningMessage: 'Do you want to discard your unsaved changes?',
            }
        });

        const deleteConfirmationDialog: DeleteConfirmationDialogComponent = dialogRef.componentInstance;

        deleteConfirmationDialog.actionConfirmed
            .pipe(take(1)).subscribe(() => {
                deleteConfirmationDialog.isBusy = true;
                dialogRef.close();
                this.populateHelperTexts();
            });

    }

    public openAddHelpTextDialog(): void {
        const dialogRef = this.dialogService.open(AddHelperTextDialogComponent, {
            width: '600px',
            maxWidth: 'none'
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe((helpText: HelpTextModel | undefined) => {
            if (helpText) {
                this.helperTexts?.push(helpText);

                const formGroup = new FormGroup({
                    helpTextId: new FormControl({value: helpText.helpTextId, disabled: true}),
                    context: new FormControl(helpText.context),
                    label: new FormControl(helpText.label),
                    contentHtmlShort: new FormControl(helpText.contentHtmlShort),
                    link: new FormControl(helpText.link, Validators.pattern(urlPattern)),
                });

                this.helperTextForms?.push(formGroup);

                setTimeout(() => {
                    document.getElementById('helpText_' + helpText.helpTextId)!
                        .scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
                });
            }

        });
    }

    public saveAll(): void {
        this.isSaving = true;
        const dirtyRows = (this.helperTextForms!.controls as FormGroup[]).filter(fg => fg.dirty);
        const helperTextsToSave = dirtyRows.map(fg => fg.getRawValue());

         this.helperTextService.saveAllHelperTexts(helperTextsToSave).pipe(take(1)).subscribe({
            next: (result) => {
                this.isSaving = false;

                for (const row of dirtyRows) {
                    row.markAsPristine();
                }
            },
            error: (err) => {
                this.errorService.addError(err, true);
                this.isSaving = false;
            }
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    public beforeUnloadHandler(event) {
        if (this.helperTextForms!.dirty) {
            event.returnValue = false;
            event.preventDefault();
            return false;
        }
    }

}
